import React, { useCallback, useState } from "react";

import { useBreakpointV2 } from "@tm/ui-widgets";

import {
  ImageComponent,
  LandingHeaderMobile,
  ReactPortalComp,
} from "../../../../components";
import FormattedText from "../../../../components/FormattedText/FormattedText";
import cancelGreyBg from "../../../../icons/cancelGreyBg.svg";
import { DownArrow } from "../../../../icons/DownArrow";
import { FAQ_CONSTANTS } from "./constants";
import {
  CloseButton,
  DrawerAnswerText,
  DrawerChevronWrapper,
  DrawerContent,
  DrawerFAQButton,
  DrawerFAQItem,
  DrawerFAQList,
  DrawerHeader,
  DrawerPanel,
  DrawerQuestionText,
  DrawerTitle,
  EmptyState,
  MobileSheetStyle,
  Overlay,
} from "./FAQDrawer.style";
import type { FAQActionCard, FAQCardProps } from "./types";

interface FAQDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  headerTitle?: FAQCardProps["headerTitle"];
  actionCards?: FAQActionCard[];
}

export function FAQDrawer({
  isOpen,
  onClose,
  headerTitle,
  actionCards,
}: FAQDrawerProps) {
  const device = useBreakpointV2();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleItem = useCallback((index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  const faqItems = actionCards ?? [];
  const resolvedTitle = headerTitle ?? FAQ_CONSTANTS.DEFAULT_TITLE;

  const faqList = (
    <DrawerContent>
      {faqItems.length === 0 ? (
        <EmptyState>{FAQ_CONSTANTS.NO_FAQS_MESSAGE}</EmptyState>
      ) : (
        <DrawerFAQList>
          {faqItems.map((faq, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <DrawerFAQItem key={faq.title ?? index}>
                <DrawerFAQButton
                  onClick={() => toggleItem(index)}
                  aria-expanded={isExpanded}
                >
                  {faq.title ? (
                    <DrawerQuestionText>{faq.title}</DrawerQuestionText>
                  ) : null}
                  <DrawerChevronWrapper $isExpanded={isExpanded}>
                    <DownArrow />
                  </DrawerChevronWrapper>
                </DrawerFAQButton>
                {isExpanded && faq.description ? (
                  <DrawerAnswerText>{faq.description}</DrawerAnswerText>
                ) : null}
              </DrawerFAQItem>
            );
          })}
        </DrawerFAQList>
      )}
    </DrawerContent>
  );

  if (device === "sm") {
    return (
      <ReactPortalComp>
        <MobileSheetStyle>
          <LandingHeaderMobile
            displayWhiteHeader
            headerTitle={
              typeof resolvedTitle === "string" ? resolvedTitle : "FAQs"
            }
            handleBackBtn={onClose}
          />
          {faqList}
        </MobileSheetStyle>
      </ReactPortalComp>
    );
  }

  return (
    <ReactPortalComp>
      <Overlay onClick={handleOverlayClick} />
      <DrawerPanel>
        <DrawerHeader>
          <DrawerTitle>
            <FormattedText value={resolvedTitle} />
          </DrawerTitle>
          <CloseButton onClick={onClose} aria-label="Close FAQ drawer">
            <ImageComponent
              src={cancelGreyBg.src}
              width="24px"
              height="24px"
              alt="Close"
            />
          </CloseButton>
        </DrawerHeader>
        {faqList}
      </DrawerPanel>
    </ReactPortalComp>
  );
}
